import { useState } from 'react';
import { Box, Typography, TextField, Button, CustomRadioGroup, DatePicker, PublicOutlinedIcon, 
    PublicOffOutlinedIcon, ImageUpload, Loader, DeleteForeverOutlinedIcon, ConfirmationDialog } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { createTrip, updateTrip, deleteTrip } from 'src/utils';

const PRIVACY_OPTIONS = [
  {
    value: 'Public',
    title: 'Public',
    description: 'Everyone can see this trip on the map.',
    privacyIcon: <PublicOutlinedIcon />,
  },
  {
    value: 'Private',
    title: 'Private',
    description: 'Only you can see this trip.',
    privacyIcon: <PublicOffOutlinedIcon />,
  },
];

export default function AddEditTrip({ 
    trip, 
    handleViewChange, 
    onTripDeleted, 
    onTripAdded, 
    onTripUpdated, 
    handleHttpSuccess, 
    handleHttpFailure, 
    showPolarstepsUrl }) {

    const isEditMode = Boolean(trip);
    const isMobile = useIsMobile();
    const formFontSize = isMobile ? 16 : 14;

    // Form state
    const [tripName, setTripName] = useState(trip?.name || '');
    const [summary, setSummary] = useState(trip?.description || '');
    const [startDate, setStartDate] = useState(trip?.startDate ? new Date(trip.startDate) : new Date());
    const [privacy, setPrivacy] = useState(trip?.privacy || 'Public');
    const [previewUrl, setPreviewUrl] = useState(trip?.coverImageUrl || null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageChanged, setImageChanged] = useState(false);

    const [polarstepsUrl, setPolarstepsUrl] = useState(trip?.polarstepsUrl || (showPolarstepsUrl ? '' : null));
    showPolarstepsUrl = showPolarstepsUrl || (trip?.polarstepsUrl !== undefined && trip?.polarstepsUrl !== null);

    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const isFormValid = tripName.trim().length > 0 && (showPolarstepsUrl ? polarstepsUrl.trim().length > 0 : true);

    const handleImageUpload = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setSelectedImage(file);
        setImageChanged(true);

        const reader = new FileReader();
        reader.onloadend = () => setPreviewUrl(reader.result);
        reader.readAsDataURL(file);
    };

    const handleClearImage = () => {
        setSelectedImage(null);
        setPreviewUrl(null);
        setImageChanged(true);
    };

    const handleCancelClick = () => {
        if (isEditMode) {
            handleViewChange('TripDetails');
        }
        else {
            handleViewChange('Overview');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!isFormValid) return;

        const payload = {
            name: tripName.trim(),
            description: summary.trim(),
            polarstepsUrl,
            startDate,
            privacy,
            removeImage: imageChanged && !selectedImage,
        };

        try {
            if (isEditMode) {
            const success = await updateTrip(trip.id, payload, selectedImage);

            if (success) {
                const updatedTrip = {
                ...trip,
                ...payload,
                coverImageUrl: selectedImage
                    ? previewUrl 
                    : (payload.removeImage ? null : trip.coverImageUrl),
                };

                onTripUpdated(updatedTrip);
                handleViewChange('TripDetails', updatedTrip);
                handleHttpSuccess('Trip updated.');
            } else {
                handleHttpFailure('Trip update failed.');
            }
            } else {
            const newTrip = await createTrip({ ...payload, image: selectedImage });
            onTripAdded(newTrip);
            handleViewChange('TripDetails', newTrip);
            handleHttpSuccess('Trip added.');
            }
        } catch (err) {
            console.error('Error saving trip:', err);
            handleHttpFailure('Error saving trip.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await deleteTrip(trip.id);
            onTripDeleted(trip.id);
            handleViewChange('Overview');
            handleHttpSuccess("Trip deleted.");
        } catch (err) {
            console.error('Error deleting trip:', err);
            handleHttpFailure('Error deleting trip.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ m: 2, height: isMobile ? undefined : '100%' }}>
        {/* Top Bar */}
        {!isMobile && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <Button disableRipple onClick={handleCancelClick} size="small">
                    Cancel
                </Button>
                <Typography variant="h5" textAlign="center" flex={1}>
                    {isEditMode ? 'Edit Trip' : 'New Trip'}
                </Typography>
                <Box width="64px" /> {/* Spacer to balance Cancel */}
            </Box>
        )}

        <Box className="scrollBox" sx={{ height: isMobile ? '100%' : '90%', px: 2 }}>
            {showPolarstepsUrl && (
                <Typography variant="subtitle1" fontWeight={500} sx={{ textAlign: 'center', lineHeight: 1.4 }}>
                    Instead of tracking a trip twice, you can add your Trip link so other travellers can see it on the map!
                </Typography>
            )}

            {/* Trip Name */}
            <Typography variant="body2" fontWeight={600} sx={{ mt: isMobile ? 2 : 3 }}>
                Trip Name:
            </Typography>
            <TextField
                fullWidth
                autoFocus
                placeholder="e.g. Alaska to Argentina"
                value={tripName}
                onChange={(e) => setTripName(e.target.value.slice(0, 50))}
                helperText={`${tripName.length}/50`}
                inputProps={{ style: { fontSize: formFontSize } }}
                margin="dense"
                required
                />

            {/* Polarsteps Url */}
            {showPolarstepsUrl && (<>
                <Typography variant="body2" fontWeight={600} sx={{ mt: isMobile ? 1 : 2 }}>
                    Your Tracking Url:
                </Typography>
                <TextField
                    fullWidth
                    placeholder="polarsteps.com/panamexplorer"
                    value={polarstepsUrl}
                    onChange={(e) => setPolarstepsUrl(e.target.value.slice(0, 100))}
                    inputProps={{ style: { fontSize: formFontSize } }}
                    margin="dense"
                    required
                    />
            </>)}

            {/* Start Date */}
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                Start Date:
            </Typography>
            <DatePicker
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                maxDate={new Date()}
                />

            {/* Trip Summary */}
            <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>
                Trip Summary:
            </Typography>
            <TextField
                fullWidth
                placeholder="What did you get up to?"
                value={summary}
                onChange={(e) => setSummary(e.target.value.slice(0, 100))}
                helperText={`${summary.length}/100`}
                inputProps={{ style: { fontSize: formFontSize } }}
                multiline
                rows={3}
                margin="dense"
                />

            {/* Image Upload */}
            <Typography variant="body2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                Trip Cover Image:
            </Typography>
            {/*<ImageUpload
                previewUrl={previewUrl}
                onUpload={handleImageUpload}
                onClear={handleClearImage} />*/}
            <ImageUpload
                previewUrl={previewUrl}
                onUpload={handleImageUpload}
                onClear={handleClearImage}
                aspect={16 / 9}
                size={200} 
                />

            {/* Privacy Settings */}
            {!showPolarstepsUrl && (<>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
                    Who can see this trip?
                </Typography>
                <CustomRadioGroup
                    selected={privacy}
                    onChange={setPrivacy}
                    options={PRIVACY_OPTIONS}
                />
            </>)}

            {/* Submit & Delete */}
            <Box mt={3} mb={4} display="flex" justifyContent="space-around" alignItems="center" sx={{width: '100%'}}>
            {isEditMode && (

                <Button 
                variant="text" 
                color="error" 
                disabled={!isFormValid}
                onClick={() => setConfirmOpen(true)}>
                    <DeleteForeverOutlinedIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>Delete Trip</Typography>
                </Button>
            )}

            <Button
                fullWidth={!isEditMode}
                variant="contained"
                disabled={!isFormValid}
                onClick={handleSubmit}
            >
                {isEditMode ? 'Update Trip' : 'Add Trip'}
            </Button>
            </Box>

        </Box>

        {loading && <Loader />}

        <ConfirmationDialog
            open={confirmOpen}
            title="Delete Trip"
            content="This action cannot be undone. Are you sure you want to delete this trip?"
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => {
                setConfirmOpen(false);
                handleDelete();
            }}
            confirmText="Delete"
            />

        </Box>
    );
}
